import { StackLayout } from "../layouts";
import {
    headers,
    DynamicReportsGrid,
    rows,
    SectionTitle,
    DataGrid,
    HeroBannerContainer,
    DataGridTabs,
} from "colbi_web_ui/lib/components";
import { parseFilter } from "colbi_web_ui/lib/utils";
import theme from "colbi_web_ui/lib/style/_theme.json";
import FindingButton from "../components/FindingButtons/RegularFindingButton";
import RawData from "../components/RawData/RawData";
import App from "../controllers/App/App";
import {
    checkForCustomFragment,
    camelizeResource,
    getEntityNameResource,
} from "../utils/queryUtils";
import GenericViewsToolbar from "../components/GenericViewsToolbar/GenericViewsToolbar";
import { getConfigVariable } from "../config/config";
import { controllerQueries } from "../controllers/utils/utils";
import AppNewLogic from "../controllers/App/AppNewLogic";
import StructuredData from "../controllers/App/StructuredData";
import { hasModule } from "../utils/utils";

const EntityByFileController = (props, useNewController, staticParams) => {
    const { modules } = props;
    const modifiedProps = { ...props, ...(staticParams ? { ...staticParams } : {}), hasHeroBanner: true };
    const Controller = hasModule(modules, "new_navigation") ? useNewController ? StructuredData : AppNewLogic : App;
    return <Controller {...modifiedProps} />;
};

EntityByFileController.queries = (args, userModulesByProject) => {
    return controllerQueries(args, userModulesByProject)
};

const queriesWrapper = (args = {}) => (queries({ ...args }));

const queries = (args = {}) => {
    const entities = (args.entities || "").split("/");
    const entityPairs = (entities || []).reduce((pairs, current) => {
        if (!pairs[pairs.length - 1] || pairs[pairs.length - 1].length === 2) {
            pairs.push([]);
        }
        if (pairs[pairs.length - 1]) {
            pairs[pairs.length - 1].push(current);
        }
        return pairs;
    }, []);
    const entityIndex = entities.length % 2 === 0 ? entityPairs.length - 1 : entityPairs.length - 2;
    const entityType = (entityPairs[entityIndex] || [])[0];
    const entityId = (entityPairs[entityIndex] || [])[1];
    const subEntityIndex = entities.length % 2 === 0 ? -1 : entityPairs.length - 1;
    const subEntityType = (entityPairs[subEntityIndex] || [])[0];

    const entity = getEntityNameResource(entityType);
    const subEntityTypeWithPrefix =
        args.modulePrefix && subEntityType
            ? `${args.modulePrefix}_${subEntityType}`
            : subEntityType;
    const entityWithPrefix =
        args.modulePrefix && entity ? `${args.modulePrefix}_${entity}` : entity;

    const customEntityFragment = checkForCustomFragment(args.modulePrefix, entityType);
    const result = [
        {
            resource: `nc_findings_for_record`,
            args: {
                projectId: args.projectId,
                fiscalYear: args.fiscalYear,
                importId: args.importId,
                referenceId: entityId,
                referenceType: entityType,
                modulePrefix: args.modulePrefix,
                locale: args.locale,
            },
            body: `__typename, items{ ...nc_findings_for_record }, summary{...nc_findings_summary}, hasMore`,
            isDatagridReq: true,
        },
        {
            resource: `adhoc_audit_notes_by_entity`,
            args: {
                projectId: args.projectId,
                fiscalYear: args.fiscalYear,
                importId: args.importId,
                referenceId: entityId,
                referenceType: entityType,
                modulePrefix: args.modulePrefix,
            },
            body: `__typename, items{...adhoc_audit_notes_by_entity}, hasMore`,
        },
    ];

    if (customEntityFragment) {
        result.push(
            {
                resource: entityWithPrefix,
                args: {
                    projectId: args.projectId,
                    fiscalYear: args.fiscalYear,
                    [`${camelizeResource(entity)}Id`]: entityId,
                    modulePrefix: args.modulePrefix,
                },
                body: `...${entityWithPrefix}`,
                isDatagridReq: true,
            },
            {
                resource: subEntityTypeWithPrefix,
                args: {
                    projectId: args.projectId,
                    fiscalYear: args.fiscalYear,
                    modulePrefix: args.modulePrefix,
                    filter: args.filter
                        ? { type: "[FilterInput!]", value: parseFilter(args.filter) }
                        : undefined,
                    sort: args.sort
                        ? { type: "[SortInput!]", value: parseFilter(args.sort) }
                        : undefined,
                    page: parseInt(args.page, 10) || 0,
                    parentId: entityId,
                    parentType: entityType ? entityType.substring(0, entityType.length - 1) : null,
                    pageSize: parseInt(
                        args.pageSize || getConfigVariable("REACT_APP_DEFAULT_PAGE_SIZE"),
                    ),
                },
                downloadable: true,
                xlsxTranslation: subEntityType,
                body: `__typename, items{...${subEntityTypeWithPrefix}}, hasMore, total, totalizers, filters(projectId:$projectId,fiscalYear:$fiscalYear,parentId:$parentId,parentType:$parentType, modulePrefix:$modulePrefix){ prop, name, type, list, disableOperators, mandatory }`,
                isDatagridReq: true,
            },
        );
    } else {
        result.push(
            {
                resource: "generic_view",
                args: {
                    projectId: args.projectId,
                    fiscalYear: args.fiscalYear,
                    importId: args.importId,
                    entity: entityType,
                    entityId: entityId,
                    modulePrefix: args.modulePrefix,
                },
                body: `__typename, details, rawData, entity, entityId, cdmEntity,entities(projectId:$projectId,fiscalYear:$fiscalYear,modulePrefix:$modulePrefix,importId:$importId,entity:$entity,entityId:$entityId){ entity, count, model }, reports(projectId:$projectId,fiscalYear:$fiscalYear,modulePrefix:$modulePrefix,importId:$importId,entity:$entity,entityId:$entityId),kpis(projectId:$projectId,fiscalYear:$fiscalYear,modulePrefix:$modulePrefix,importId:$importId,entity:$entity,entityId:$entityId)`,
                isDatagridReq: true,
            },
            //TODO: rerender in detail view
            {
                resource: "generic_views",
                args: {
                    projectId: args.projectId,
                    fiscalYear: args.fiscalYear,
                    importId: args.importId,
                    modulePrefix: args.modulePrefix,
                    filter: args.filter
                        ? { type: "[FilterInput!]", value: parseFilter(args.filter) }
                        : undefined,
                    sort: args.sort
                        ? { type: "[SortInput!]", value: parseFilter(args.sort) }
                        : undefined,
                    page: parseInt(args.page, 10) || 0,
                    entity: entityType,
                    entityId: entityId,
                    subEntity: subEntityType ? subEntityType : "",
                    pageSize: parseInt(
                        args.pageSize || getConfigVariable("REACT_APP_DEFAULT_PAGE_SIZE"),
                    ),
                },
                body: `__typename, items, headerItems, hasMore, total, totalizers, entity, filters(projectId:$projectId,fiscalYear:$fiscalYear,importId:$importId,entityId:$entityId,subEntity:$subEntity,modulePrefix:$modulePrefix){ prop, name, type, list, disableOperators, mandatory }`,
                isDatagridReq: true,
            },
            {
                resource: "generic_views_export",
                args: {
                    projectId: args.projectId,
                    fiscalYear: args.fiscalYear,
                    importId: args.importId,
                    modulePrefix: args.modulePrefix,
                    entity: entityType,
                    entityId: entityId,
                    subEntity: subEntityType ? subEntityType : "",
                    filter: args.filter
                        ? { type: "[FilterInput!]", value: parseFilter(args.filter) }
                        : undefined,
                    sort: args.sort
                        ? { type: "[SortInput!]", value: parseFilter(args.sort) }
                        : undefined,
                    page: parseInt(args.page, 10) || 0,
                },
                body: `__typename, items, headerItems hasMore, total, totalizers, entity, filters(projectId:$projectId,fiscalYear:$fiscalYear,importId:$importId,entityId:$entityId,subEntity:$subEntity,modulePrefix:$modulePrefix){ prop, name, type, list, disableOperators, mandatory }`,
                downloadable: true,
                downloadOnly: true,
                xlsxTranslation: subEntityType ? subEntityType : "",
            },
        );
    }

    return result.filter((q) => !!q.resource);
};

const entityByFile = (path, navigation, exact, navByImportId = false, staticParams = {}, useNewController = false) => {
    const ControllerWrapper = (props) =>
        EntityByFileController(props, useNewController, { ...staticParams });
    ControllerWrapper.queries = EntityByFileController.queries;

    return {
        path,
        navigation,
        component: ControllerWrapper,
        queries: (args = {}) => queriesWrapper({ ...staticParams, ...args }),
        exact,
        navByImportId,
        staticParams,
        title: ["label(query(2).entity)", "query(2).shortName"].filter((l) => l),
        //TODO Check if this is needed
        headerComponentConfigs: {
            showButton: true,
            label: "Download",
        },
        headerComponents: [
            {
                component: RawData,
                props: {
                    label: "Raw Data",
                    data: "query(2).rawData",
                    entity: "query(2).entity",
                },
            },
            {
                component: FindingButton,
                props: {
                    projectId: "param(projectId)",
                    fiscalYear: "param(fiscalYear)",
                    importId: "param(importId)",
                    modulePrefix: "param(modulePrefix)",
                    ruleDescription: "label(New Audit Note)",
                    origin: "A",
                    entityName: "query(2).__typename",
                    entityId: "query(2).entityId",
                },
            },
        ],
        components: [
            {
                component: HeroBannerContainer,
                modifiers: ["--root-margin-bottom-small"],
                props: {
                    icon: "audit-icon",
                    title: "label(Related Audit)",
                    summary: "query(0).metadata.summary",
                    expanded: false,
                    children: [
                        {
                            component: SectionTitle,
                            props: {
                                title: "Rules Findings",
                                modifiers: ["--margin-bottom-top-title"],
                            },
                        },
                        {
                            component: DataGrid,
                            props: {
                                basePath:
                                    "/:locale(pt|en|lt|es|ro|fr)?/:projectId/:fiscalYear([0-9]{4}|current)/:importId/nc_audit/:modulePrefix/rules",
                                items: "query(0).list",
                                card: rows.CellCard,
                                header: "query(0).header",
                                status: "status(0)",
                            },
                        },
                        {
                            component: SectionTitle,
                            props: {
                                title: "Audit Notes",
                                modifiers: ["--margin-bottom-top-title"],
                            },
                        },
                        {
                            component: DataGrid,
                            props: {
                                items: "query(1).list",
                                card: rows.CellCard,
                                header: "query(1).header",
                                status: "status(0)",
                            },
                        },
                    ],
                },
            },
            {
                component: headers.EntityHeader,
                modifiers: ["--root-margin-bottom-normal"],
                props: {
                    type: "query(2).__typename",
                    data: "query(2).details",
                },
            },
            {
                component: DynamicReportsGrid,
                modifiers: [
                    "--root-margin-bottom-normal",
                    "--cardholder-background-color-widget",
                    "--cardholder-aspect-ratio-16x9",
                ],
                props: {
                    type: "query(2).__typename",
                    data: "query(2).reports",
                },
            },
            {
                component: DataGridTabs,
                modifiers: ["--root-margin-top-small", "--root-margin-bottom-small"],
                props: {
                    basePath: `${path}`,
                    showCount: true,
                    data: "query(2).entities",
                    labelKey: "entity",
                    routeKey: "model",
                    redirectEnabler: true,
                    stickyTop: Math.min(39, parseInt(theme.layout.$headerHeight, 10) || 39),
                    preserveEmptyTabs: true,
                },
            },
        ],
        routes: [
            {
                path,
                exact: true,
                queries: (args = {}) => (queriesWrapper({ ...staticParams, ...args })),
                component: StackLayout,
                components: [
                    {
                        component: DataGrid,
                        props: {
                            id: "results",
                            card: rows.CellCard,
                            items: "query(3).list",
                            /**
                            * This is used to validate if the datagrid is going to be shown in detail views
                            * entities
                            * details
                            */
                            entities: "query(2).entities",
                            details: "query(2).details",
                            header: "query(3).header",
                            footer: "query(3).footer",
                            metadata: "query(3).metadata",
                            page: "param(page)",
                            status: "status(0)",
                            sort: "param(sort)",
                            pageSize: "param(pageSize)",
                            pageDefault: 10,
                            filter: "param(filter)",
                            availableFilters: "query(3).filters",
                            wrapper: "wrapper",
                            showCheckbox: "query(3).showCheckbox",
                            total: "query(3).metadata.total",
                            projectModules: "query(1)",
                            totalizers: "query(3).totalizers",
                            toolbar: GenericViewsToolbar,
                            basePath: path,
                            toolbarProps: {
                                data: "query(3).list",
                            },
                        },
                    },
                ],
            },
        ],
    };
};

export default entityByFile;
